import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PrimaryButton } from '@/components/glitch-ui/primary-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/useToast';
import { ScheduledJob, useUpdateJobMutation } from '@/lib/api-extensions/scheduler-queries';
import { CATCHUP_POLICIES, OVERLAP_POLICIES, QUEUES, TIMEZONES } from './scheduler-constants';

interface EditJobDialogProps {
  job: ScheduledJob | null;
  open: boolean;
  onClose: () => void;
}

/** Edit one job's schedule, timezone, queue, run policies, and timeout. */
export function EditJobDialog({ job, open, onClose }: EditJobDialogProps) {
  const updateJob = useUpdateJobMutation();
  const [schedule, setSchedule] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [queue, setQueue] = useState('default');
  const [overlap, setOverlap] = useState('skip');
  const [catchup, setCatchup] = useState('fire_once');
  const [maxDuration, setMaxDuration] = useState('3600');

  // Reset the form to the selected job whenever a different row opens the dialog.
  useEffect(() => {
    if (!job) return;
    setSchedule(job.schedule);
    setTimezone(job.timezone);
    setQueue(job.queue);
    setOverlap(job.overlap_policy);
    setCatchup(job.catchup_policy);
    setMaxDuration(String(job.max_duration_sec));
  }, [job]);

  if (!job) return null;

  const save = () => {
    const seconds = parseInt(maxDuration, 10);
    if (Number.isNaN(seconds) || seconds <= 0) {
      toast({
        title: 'Invalid value',
        description: 'Max duration must be a positive number of seconds.',
        variant: 'destructive',
      });
      return;
    }
    updateJob.mutate(
      {
        jobId: job.id,
        update: {
          schedule,
          timezone,
          queue,
          overlap_policy: overlap,
          catchup_policy: catchup,
          max_duration_sec: seconds,
        },
      },
      {
        onSuccess: () => {
          toast({ title: 'Job updated', description: `${job.job_name} has been saved.` });
          onClose();
        },
        onError: (e) =>
          toast({
            title: 'Update failed',
            description: e instanceof Error ? e.message : 'Could not update the job.',
            variant: 'destructive',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Schedule</Label>
            <Input
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              placeholder="0 9 * * *"
              className="font-mono"
            />
            <p className="text-xs text-gray-500">
              A 5-field cron, or INHERIT_ORG to follow the organization clock.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {TIMEZONES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Queue</Label>
              <Select value={queue} onValueChange={setQueue}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUEUES.map((q) => (
                    <SelectItem key={q} value={q}>
                      {q}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Overlap Policy</Label>
              <Select value={overlap} onValueChange={setOverlap}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OVERLAP_POLICIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Catchup Policy</Label>
              <Select value={catchup} onValueChange={setCatchup}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATCHUP_POLICIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Max Duration (seconds)</Label>
            <Input type="number" value={maxDuration} onChange={(e) => setMaxDuration(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateJob.isPending}>
            Cancel
          </Button>
          <PrimaryButton
            label={updateJob.isPending ? 'Saving...' : 'Save'}
            onClick={save}
            disabled={updateJob.isPending}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
